<template>
  <main class="tohumlar-page">
    <section class="hero" :style="{ backgroundImage: `url(${hero.image})` }">
      <div class="hero-shade"></div>
      <div class="hero-inner">
        <div class="hero-copy">
          <img :src="brand.logo" :alt="brand.name" class="brand-logo" />
          <p class="eyebrow">{{ hero.kicker }}</p>
          <h1>{{ hero.title }}</h1>
          <p class="lead">{{ hero.lead }}</p>
          <div class="hero-actions">
            <a href="#tohum-aileleri" class="btn primary">Tohumları İncele</a>
            <router-link to="/iletisim" class="btn secondary">Distribütörlük İletişimi</router-link>
          </div>
        </div>
        <div class="hero-facts" aria-label="May Tohum distribütörlük özeti">
          <div class="fact">
            <strong>Karadeniz</strong>
            <span>Distribütörlük bölgesi</span>
          </div>
          <div class="fact">
            <strong>Tarla + Sebze</strong>
            <span>Ürün aileleri</span>
          </div>
          <div class="fact">
            <strong>OCN Gübre</strong>
            <span>Besleme ve saha desteği</span>
          </div>
        </div>
      </div>
    </section>

    <section class="section intro-section">
      <div class="section-header">
        <p class="eyebrow dark">May Tohum iş birliği</p>
        <h2>Gübre portföyümüzü güçlü tohum genetiğiyle tamamlıyoruz</h2>
        <p class="section-lead">
          May Tohum'un marka değeri ve çeşit portföyü, OCN Gübre'nin bölgesel saha deneyimiyle birleşiyor. Üreticiye
          yalnızca ürün değil; doğru çeşit, doğru besleme ve sezon boyunca teknik takip sunuyoruz.
        </p>
      </div>
      <div class="pillar-grid">
        <article v-for="pillar in servicePillars" :key="pillar.title" class="pillar-card">
          <h3>{{ pillar.title }}</h3>
          <p>{{ pillar.copy }}</p>
        </article>
      </div>
    </section>

    <section id="tohum-aileleri" class="section">
      <div class="section-header with-filters">
        <div>
          <p class="eyebrow dark">Ürün aileleri</p>
          <h2>May Tohum çeşitleri</h2>
          <p class="section-lead">
            Aşağıdaki ürün aileleri May Tohum'un resmi ürün sayfalarından derlenmiştir. Detay çeşit bilgileri için ilgili
            May Tohum sayfasına geçebilirsiniz.
          </p>
        </div>
        <div class="filters" aria-label="Tohum grubu filtreleri">
          <button
            v-for="group in groups"
            :key="group"
            :class="['filter-chip', { active: activeGroup === group }]"
            @click="activeGroup = group"
          >
            {{ group }}
          </button>
        </div>
      </div>

      <div class="seed-grid">
        <article v-for="family in filteredFamilies" :key="family.name" class="seed-card">
          <div class="seed-image-wrap">
            <img :src="family.image_url" :alt="`${family.name} tohumu`" class="seed-image" />
            <span class="group-pill">{{ family.group }}</span>
          </div>
          <div class="seed-body">
            <h3>{{ family.name }}</h3>
            <p>{{ family.description }}</p>
            <div class="mini-list">
              <span v-for="filter in family.filters" :key="filter">{{ filter }}</span>
            </div>
            <div class="featured">
              <span v-for="item in family.featured" :key="item">{{ item }}</span>
            </div>
          </div>
          <a :href="family.source_url" target="_blank" rel="noopener noreferrer" class="source-link">
            May Tohum'da İncele
          </a>
        </article>
      </div>
    </section>

    <section class="section contact-band">
      <div>
        <p class="eyebrow light">Karadeniz üreticisi için</p>
        <h2>Tohum seçimi, gübreleme ve saha takibini tek noktadan planlayalım.</h2>
      </div>
      <router-link to="/iletisim" class="contact-link">İletişime Geç</router-link>
    </section>
  </main>
</template>

<script>
import seedData from '@/assets/seed_products.json';

export default {
  name: 'AppTohumlar',
  data() {
    return {
      brand: seedData.brand,
      hero: seedData.hero,
      families: seedData.families,
      servicePillars: seedData.servicePillars,
      activeGroup: 'Hepsi'
    };
  },
  computed: {
    groups() {
      const set = new Set(['Hepsi']);
      this.families.forEach((family) => set.add(family.group));
      return Array.from(set);
    },
    filteredFamilies() {
      if (this.activeGroup === 'Hepsi') return this.families;
      return this.families.filter((family) => family.group === this.activeGroup);
    }
  }
};
</script>

<style scoped>
.tohumlar-page {
  background: #f7fafc;
  color: #0f172a;
  min-height: 100vh;
}

.hero {
  position: relative;
  min-height: 520px;
  background-size: cover;
  background-position: center;
  display: grid;
  align-items: end;
  overflow: hidden;
}

.hero-shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(7, 20, 35, 0.82) 0%, rgba(7, 20, 35, 0.58) 48%, rgba(7, 20, 35, 0.18) 100%);
}

.hero-inner {
  position: relative;
  width: var(--page-width);
  margin: 0 auto;
  padding: 72px 0 48px;
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.7fr);
  gap: 28px;
  align-items: end;
}

.hero-copy {
  text-align: left;
  color: #ffffff;
  display: grid;
  gap: 14px;
  max-width: 760px;
}

.brand-logo {
  width: 118px;
  height: auto;
  background: #ffffff;
  border-radius: 8px;
  padding: 8px;
}

.eyebrow {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 800;
  color: #b8e04a;
}

.eyebrow::before {
  content: '';
  width: 18px;
  height: 2px;
  background: currentColor;
}

.eyebrow.dark {
  color: #0075b8;
}

.eyebrow.light {
  color: #b8e04a;
}

h1,
h2,
h3 {
  margin: 0;
}

h1 {
  font-size: 44px;
  line-height: 1.08;
  max-width: 780px;
}

.lead {
  margin: 0;
  color: #eef6ff;
  line-height: 1.65;
  max-width: 720px;
  font-size: 17px;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
}

.btn,
.contact-link,
.source-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  min-height: 44px;
  padding: 10px 16px;
  font-weight: 800;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
}

.btn.primary {
  background: #b8e04a;
  color: #0f2f18;
  border: 1px solid #b8e04a;
}

.btn.secondary {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.btn:hover,
.contact-link:hover,
.source-link:hover {
  transform: translateY(-2px);
}

.hero-facts {
  display: grid;
  gap: 10px;
}

.fact {
  text-align: left;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.68);
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 18px 36px rgba(7, 20, 35, 0.22);
}

.fact strong {
  display: block;
  font-size: 20px;
  color: #0075b8;
  margin-bottom: 4px;
}

.fact span {
  color: #334155;
  font-size: 13px;
  font-weight: 700;
}

.section {
  width: var(--page-width);
  margin: 0 auto;
  padding: 54px 0;
}

.intro-section {
  display: grid;
  gap: 20px;
}

.section-header {
  text-align: left;
  display: grid;
  gap: 8px;
}

.section-header h2 {
  font-size: 30px;
  line-height: 1.2;
}

.section-lead {
  margin: 0;
  color: #334155;
  line-height: 1.65;
  max-width: 820px;
}

.with-filters {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 16px;
  margin-bottom: 18px;
}

.pillar-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 14px;
}

.pillar-card,
.seed-card {
  background: #ffffff;
  border: 1px solid #dbe4ee;
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
}

.pillar-card {
  text-align: left;
  padding: 18px;
}

.pillar-card h3 {
  font-size: 18px;
  margin-bottom: 8px;
}

.pillar-card p {
  margin: 0;
  color: #334155;
  line-height: 1.6;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.filter-chip {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a;
  padding: 9px 12px;
  border-radius: 8px;
  font-weight: 800;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.filter-chip:hover,
.filter-chip.active {
  border-color: #0075b8;
  background: #0075b8;
  color: #ffffff;
}

.seed-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.seed-card {
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr auto;
  text-align: left;
}

.seed-image-wrap {
  position: relative;
  height: 210px;
  background: #e8f2f8;
  overflow: hidden;
}

.seed-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.group-pill {
  position: absolute;
  left: 12px;
  bottom: 12px;
  background: #ffffff;
  color: #0075b8;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 800;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.18);
}

.seed-body {
  padding: 16px;
  display: grid;
  gap: 12px;
}

.seed-body h3 {
  font-size: 22px;
}

.seed-body p {
  margin: 0;
  color: #334155;
  line-height: 1.55;
}

.mini-list,
.featured {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.mini-list span {
  background: #e8f5fc;
  color: #075985;
  border: 1px solid #c7e8f7;
  border-radius: 8px;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 700;
}

.featured span {
  background: #f7fbef;
  color: #365314;
  border: 1px solid #d9edb5;
  border-radius: 8px;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 800;
}

.source-link {
  margin: 0 16px 16px;
  background: #0f172a;
  color: #ffffff;
  border: 1px solid #0f172a;
}

.source-link:hover {
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
}

.contact-band {
  background: #0f172a;
  color: #ffffff;
  border-radius: 8px;
  padding: 28px;
  margin-bottom: 54px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  text-align: left;
}

.contact-band h2 {
  max-width: 760px;
  font-size: 26px;
  line-height: 1.25;
  margin-top: 8px;
}

.contact-link {
  flex-shrink: 0;
  background: #b8e04a;
  color: #102a12;
  border: 1px solid #b8e04a;
}

@media (max-width: 920px) {
  .hero {
    min-height: auto;
  }

  .hero-inner {
    grid-template-columns: 1fr;
    padding: 48px 0 32px;
  }

  h1 {
    font-size: 34px;
  }

  .with-filters {
    grid-template-columns: 1fr;
  }

  .filters {
    justify-content: flex-start;
  }
}

@media (max-width: 640px) {
  .hero-copy {
    gap: 12px;
  }

  .brand-logo {
    width: 96px;
  }

  h1 {
    font-size: 30px;
  }

  .lead {
    font-size: 15px;
  }

  .hero-actions {
    flex-direction: column;
  }

  .section {
    width: calc(100% - 24px);
    padding: 38px 0;
  }

  .section-header h2 {
    font-size: 24px;
  }

  .seed-grid {
    grid-template-columns: 1fr;
  }

  .contact-band {
    width: calc(100% - 24px);
    box-sizing: border-box;
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 38px;
  }

  .contact-band h2 {
    font-size: 22px;
  }
}
</style>
